import React, { useMemo, useRef, useState } from 'react';
import { Flex } from 'rebass';
import { WaterServiceComment } from './WaterServiceDetail';
import { Textarea } from '@rebass/forms';
import { useMutation } from '@apollo/client';
import { sortHistoryItems } from '../Timeline/helpers';

import Timeline from '../Timeline/Timeline';
import { HistoryItem } from '../Timeline/types';
import { convertWaterServiceComment } from './helpers';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '../RequirePerms';

type WaterServiceHistoryProps = {
  waterServiceId: string;
  comments: WaterServiceComment[];
  refetch?: () => void;
};

const CREATE_WATER_SERVICE_COMMENT = gql(`
  mutation CreateWaterServiceComment($input: WaterServiceCommentInput!) {
    createWaterServiceComment(input: $input) {
      id
    }
  }
`);

const WaterServiceHistory: React.FC<WaterServiceHistoryProps> = ({
  comments,
  waterServiceId,
  refetch,
}) => {
  const [createWaterServiceComment] = useMutation(
    CREATE_WATER_SERVICE_COMMENT,
    {}
  );
  const [comment, setComment] = useState('');

  const scrollDiv = useRef<any>();

  const amendedHistoryItems = useMemo<HistoryItem<'COMMENT'>[]>(() => {
    const items: HistoryItem<'COMMENT'>[] = [
      ...comments.map(convertWaterServiceComment),
    ];

    // Convert, concat, filter, sort and group
    return items.slice().sort(sortHistoryItems);
  }, [comments]);

  return (
    <Flex flexDirection="column" height="100%" flexGrow={1} flexShrink={1}>
      <Timeline
        noRail
        title="Notes"
        historyItems={amendedHistoryItems}
        scrollDivRef={scrollDiv}
      />

      {/************************************
       * Message Box
       ************************************/}
      <RequirePerms waterServices="UPDATE">
        <Flex
          sx={{ borderTop: '1px solid', borderTopColor: 'tableBorder' }}
          flexGrow={0}
          flexShrink={0}
          p={4}
        >
          <Textarea
            height={34}
            value={comment}
            onBlur={(e) => {
              if (!e.target.value) {
                e.target.style.height = '34px';
              }
            }}
            onFocus={(e) => {
              e.target.style.height = '66px';
            }}
            onKeyPress={async (e) => {
              if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                e.preventDefault();
                if (comment.trim().length > 0) {
                  await createWaterServiceComment({
                    variables: {
                      input: {
                        waterServiceId,
                        comment,
                      },
                    },
                  });
                  setComment('');
                  if (refetch) {
                    await refetch();
                  }
                  scrollDiv.current.scrollTop = 0;
                }
              }
            }}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder="Enter note"
            sx={{ flexGrow: 1, transition: 'height .3s' }}
          />
        </Flex>
      </RequirePerms>
    </Flex>
  );
};

export default WaterServiceHistory;
